import React, { useEffect, FC } from "react";
import { useTranslation } from "react-i18next";
import { FormikConfig } from "formik";
import { history } from "src/services/history";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { InquiryForm } from "src/page-components/cabinet/my-inquiries/InquirySettings/InquiryForm";
import { INQUIRY_SETTINGS_VALIDATION_SCHEMA } from "src/page-components/cabinet/my-inquiries/InquirySettings/constants";
import { inquirySettingsTrns } from "src/page-components/cabinet/my-inquiries/InquirySettings/trns";
import { InquirySettingsValues } from "src/page-components/cabinet/my-inquiries/InquirySettings/types";
import { PageWrapper } from "src/components/Layouts/PageWrapper";
import { editInquiryAsync } from "src/redux/myInquiries/actions";
import { getCurrentInquiryAsync } from "src/redux/currentInquiry/actions";
import { selectCurrentInquiry } from "src/redux/currentInquiry/selectors";
import { selectCurrentUser } from "src/redux/currentUser/selector";
import { getItemPath } from "src/utils/getItemPath";
import { PATHNAMES } from "src/constants/routes";
import { MetadataWrapper } from "src/components/MetadataWrapper";
import { combineSlugId } from "src/utils/combineSlugId";
import { useExtractId } from "src/hooks/useExractId";
import { INITIAL_VALUES } from "../create";
import { ScreenLoader } from "src/components/Loader/ScreenLoader";

const EditInquiry: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const id = useExtractId();

  const { item: inquiry, isLoading } = useAppSelector(selectCurrentInquiry);
  const user = useAppSelector(selectCurrentUser);

  useEffect(() => {
    dispatch(getCurrentInquiryAsync({ id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (inquiry && user) {
      const isOwner = user.host.email === inquiry.host.email;
      if (!isOwner) history.push(PATHNAMES.HOME, { replace: true });
    }
  }, [inquiry, user]);

  if (isLoading) return <ScreenLoader />;
  if (!inquiry) return null;

  const formikProps: FormikConfig<InquirySettingsValues> = {
    initialValues: {
      ...inquiry,
      industry: inquiry.industry.slug,
      inquiry_type: inquiry.inquiry_type.slug,
    },
    validationSchema: INQUIRY_SETTINGS_VALIDATION_SCHEMA,
    onSubmit: (values, { resetForm }) => {
      const onSuccess = () => {
        const combinedSlug = combineSlugId(inquiry.slug, inquiry.id);
        history.push(
          getItemPath(PATHNAMES.INQUIRY_ITEM, { slug: combinedSlug }),
          {
            replace: true,
          }
        );
        resetForm({ values: INITIAL_VALUES });
      };

      dispatch(editInquiryAsync({ values, id, onSuccess }));
    },
  };

  return (
    <MetadataWrapper
      title={inquiry?.inquiry_meta_title}
      description={inquiry?.inquiry_meta_title}
    >
      <PageWrapper mainClassName="flex flex-col gap-10 py-25">
        <InquiryForm
          formikProps={formikProps}
          title={t(inquirySettingsTrns.changeInformationTitle)}
        />
      </PageWrapper>
    </MetadataWrapper>
  );
};

export default EditInquiry;
